/**
 * AI 服务配置管理页面
 */

import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Card,
  Form,
  Input,
  Modal,
  message,
  Tag,
  Switch,
  InputNumber,
  Select,
  Typography,
  Row,
  Col
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  RobotOutlined,
  CheckCircleOutlined,
  SyncOutlined
} from '@ant-design/icons';
import {
  getAiServiceList,
  addAiService,
  updateAiService,
  deleteAiService,
  setDefaultService,
  testConnection
} from '../services/api/aiService';
import { useModal } from '../hooks';
import type { AiServiceConfig } from '../services/api/aiService';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const AiServiceConfigPage: React.FC = () => {
  const modal = useModal();
  const [form] = Form.useForm();
  const [services, setServices] = useState<AiServiceConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingService, setEditingService] = useState<AiServiceConfig | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  // 获取 AI 服务列表
  const fetchServices = async () => {
    setLoading(true);
    try {
      const list = await getAiServiceList();
      // 按优先级排序
      list.sort((a, b) => a.priority - b.priority);
      setServices(list);
    } catch (error: any) {
      console.error('获取 AI 服务列表失败:', error);
      message.error('获取 AI 服务列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  // 删除服务
  const handleDelete = async (service: AiServiceConfig) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除 AI 服务 "${service.serviceName}" 吗？`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteAiService(service.id);
          message.success('服务删除成功');
          fetchServices();
        } catch (error: any) {
          console.error('删除服务失败:', error);
          message.error(error.message || '删除服务失败');
        }
      }
    });
  };

  // 设置默认服务
  const handleSetDefault = async (service: AiServiceConfig) => {
    try {
      await setDefaultService(service.id);
      message.success('默认服务设置成功');
      fetchServices();
    } catch (error: any) {
      console.error('设置默认服务失败:', error);
      message.error(error.message || '设置默认服务失败');
    }
  };

  // 测试连接
  const handleTestConnection = async (service: AiServiceConfig) => {
    setTestingId(service.id);
    try {
      await testConnection(service);
      message.success('连接测试成功！');
    } catch (error: any) {
      console.error('连接测试失败:', error);
      message.error(error.message || '连接测试失败');
    } finally {
      setTestingId(null);
    }
  };

  // 保存服务
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const data: AiServiceConfig = {
        ...values,
        id: editingService?.id || ''
      };

      if (editingService) {
        await updateAiService(data);
        message.success('服务更新成功');
      } else {
        await addAiService(data);
        message.success('服务创建成功');
      }

      modal.hideModal();
      form.resetFields();
      setEditingService(null);
      fetchServices();
    } catch (error: any) {
      console.error('操作失败:', error);
      if (error.message) {
        message.error(error.message);
      }
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '🤖 服务名称',
      dataIndex: 'serviceName',
      key: 'serviceName',
      width: 180,
      render: (text: string, record: AiServiceConfig) => (
        <Space>
          <RobotOutlined style={{ color: record.isEnabled ? '#52c41a' : '#d9d9d9' }} />
          <Text strong>{text}</Text>
          {record.isDefault && (
            <Tag color="blue">默认</Tag>
          )}
        </Space>
      )
    },
    {
      title: '📡 服务类型',
      dataIndex: 'serviceType',
      key: 'serviceType',
      width: 100,
      align: 'center' as const,
      render: (type: 'LOCAL' | 'CLOUD') => (
        <Tag color={type === 'LOCAL' ? 'green' : 'orange'}>
          {type === 'LOCAL' ? '☁️ 本地' : '🌐 云端'}
        </Tag>
      )
    },
    {
      title: '🔗 API 地址',
      dataIndex: 'apiUrl',
      key: 'apiUrl',
      width: 250,
      ellipsis: true
    },
    {
      title: '📦 模型名称',
      dataIndex: 'modelName',
      key: 'modelName',
      width: 150
    },
    {
      title: '⚡ 优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      align: 'center' as const,
      render: (priority: number) => <Tag>{priority}</Tag>
    },
    {
      title: '🕒 超时 (ms)',
      dataIndex: 'timeoutMs',
      key: 'timeoutMs',
      width: 90,
      align: 'center' as const,
      render: (timeout?: number) => timeout || '-'
    },
    {
      title: '📈 状态',
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      width: 100,
      align: 'center' as const,
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'success' : 'default'}>
          {enabled ? '✅ 启用' : '⏸️ 禁用'}
        </Tag>
      )
    },
    {
      title: '📝 备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 150,
      ellipsis: true
    },
    {
      title: '⚙️ 操作',
      key: 'action',
      width: 280,
      fixed: 'right' as const,
      render: (_: any, record: AiServiceConfig) => (
        <Space size="small" direction="vertical">
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingService(record);
                form.setFieldsValue(record);
                modal.showModal();
              }}
            >
              编辑
            </Button>
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            >
              删除
            </Button>
          </Space>
          <Space size="small">
            <Button
              type="link"
              size="small"
              disabled={record.isDefault}
              onClick={() => handleSetDefault(record)}
            >
              设为默认
            </Button>
            <Button
              type="link"
              size="small"
              icon={testingId === record.id ? <SyncOutlined spin /> : <CheckCircleOutlined />}
              onClick={() => handleTestConnection(record)}
              loading={testingId === record.id}
            >
              测试连接
            </Button>
          </Space>
        </Space>
      )
    }
  ];

  return (
    <div className="ai-service-config-page">
      <Card
        title={
          <Space>
            <RobotOutlined />
            <span>AI 服务配置</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingService(null);
              form.resetFields();
              form.setFieldsValue({
                serviceType: 'LOCAL',
                timeoutMs: 30000,
                priority: 1,
                isEnabled: true
              });
              modal.showModal();
            }}
          >
            ➕ 新增服务
          </Button>
        }
        bordered={false}
      >
        <Table
          dataSource={services}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 新增/编辑服务模态框 */}
      <Modal
        title={editingService ? '✏️ 编辑 AI 服务' : '➕ 新增 AI 服务'}
        open={modal.visible}
        onOk={handleSave}
        onCancel={modal.handleCancel}
        confirmLoading={modal.confirmLoading}
        okText="保存"
        cancelText="取消"
        width={700}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="serviceName"
                label="🤖 服务名称"
                rules={[{ required: true, message: '请输入服务名称!' }]}
              >
                <Input placeholder="例如：GPT-4、文心一言" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="serviceType"
                label="📡 服务类型"
                rules={[{ required: true, message: '请选择服务类型!' }]}
              >
                <Select>
                  <Option value="LOCAL">☁️ 本地部署</Option>
                  <Option value="CLOUD">🌐 云端服务</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="apiUrl"
            label="🔗 API 地址"
            rules={[
              { required: true, message: '请输入 API 地址!' },
              { type: 'url', message: '请输入有效的 URL 地址!' }
            ]}
          >
            <Input placeholder="https://api.example.com/v1/chat" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="apiKey"
                label="🔑 API Key"
              >
                <Input.Password placeholder="请输入 API Key" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="modelName"
                label="📦 模型名称"
                rules={[{ required: true, message: '请输入模型名称!' }]}
              >
                <Input placeholder="例如：gpt-4、ernie-bot" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="timeoutMs"
                label="🕒 超时时间 (ms)"
              >
                <InputNumber
                  min={1000}
                  step={1000}
                  style={{ width: '100%' }}
                  placeholder="默认 30000"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="priority"
                label="⚡ 优先级"
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="数字越小优先级越高"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="isEnabled"
                label="📈 状态"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="✅ 启用"
                  unCheckedChildren="⏸️ 禁用"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="remark"
            label="📝 备注"
          >
            <TextArea
              rows={2}
              placeholder="请输入备注信息"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AiServiceConfigPage;
